import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useXR } from "@react-three/xr";
import * as THREE from "three";
import { RoomReflections } from "./RoomReflections";
import { rotateLightHarmonics, validLightEstimate } from "./roomLightingMath";

export default function XrRoomLighting({ enabled, lighting }) {
  const session = useXR(s => s.session);
  const player = useXR(s => s.player);
  const gl = useThree(s => s.gl);
  const active = useRef(null);
  const rig = useMemo(() => {
    const group = new THREE.Group(); group.name = "xr-room-lighting";
    const probe = new THREE.LightProbe(); probe.intensity = 0;
    const sun = new THREE.DirectionalLight(0xffffff, 0);
    group.add(probe, sun, sun.target);
    return { group, probe, sun, sh: new THREE.SphericalHarmonics3(), matrix: new THREE.Matrix4(),
      quaternion: new THREE.Quaternion(), direction: new THREE.Vector3(), color: new THREE.Color() };
  }, []);
  useEffect(() => {
    const state = { alive: true, probe: null, reflections: null, captured: false };
    active.current = state;
    lighting.current = { environment: null, status: enabled ? "fallback" : "off" };
    rig.probe.intensity = 0; rig.sun.intensity = 0;
    rig.group.userData.status = lighting.current.status;
    const stop = () => {
      state.alive = false;
      lighting.current = { environment: null, status: "off" };
      rig.probe.intensity = 0; rig.sun.intensity = 0;
      rig.group.userData.status = "off";
      state.reflections?.dispose(); state.reflections = null;
    };
    if (enabled && session?.requestLightProbe) {
      (async () => {
        try {
          const probe = await session.requestLightProbe({ reflectionFormat: session.preferredReflectionFormat || "srgba8" });
          if (!state.alive) return;
          state.probe = probe;
          if (window.XRWebGLBinding) {
            try { state.reflections = new RoomReflections(gl, session); }
            catch { /* Light estimates remain useful without reflection support. */ }
          }
        } catch { /* Optional feature denied/unsupported: retain showroom lighting. */ }
      })();
    }
    session?.addEventListener("end", stop);
    return () => { stop(); session?.removeEventListener("end", stop); };
  }, [enabled, session, gl, lighting, rig]);

  useFrame((_, __, frame) => {
    const state = active.current;
    if (!enabled || !state?.alive || state.captured) return;
    let estimate, pose;
    if (state.probe && frame?.session === session && session.visibilityState === "visible") {
      try {
        estimate = frame.getLightEstimate?.(state.probe);
        pose = frame.getPose(state.probe.probeSpace, gl.xr.getReferenceSpace());
      } catch { /* Temporary tracking loss or unavailable feature. */ }
    }
    const valid = validLightEstimate(estimate) && pose;
    if (valid) {
      // Freeze the first usable sample for this Car-only activation. In
      // particular, head motion and reflectionchange must not relight the car.
      player.updateWorldMatrix(true, false);
      rig.matrix.fromArray(pose.transform.matrix).premultiply(player.matrixWorld);
      rig.quaternion.setFromRotationMatrix(rig.matrix);
      rotateLightHarmonics(estimate.sphericalHarmonicsCoefficients, rig.quaternion, rig.sh);
      rig.probe.sh.copy(rig.sh);
      const rgb = estimate.primaryLightIntensity;
      const intensity = Math.max(rgb.x, rgb.y, rgb.z);
      rig.color.setRGB(rgb.x / (intensity || 1), rgb.y / (intensity || 1), rgb.z / (intensity || 1));
      rig.sun.color.copy(rig.color);
      rig.sun.intensity = intensity;
      rig.direction.copy(estimate.primaryLightDirection).applyQuaternion(rig.quaternion).normalize();
      rig.sun.position.copy(rig.direction).multiplyScalar(10);
      if (state.reflections) {
        try {
          lighting.current.environment = state.reflections.update(state.probe, rig.quaternion);
        } catch {
          lighting.current.environment = null;
          state.reflections.dispose(); state.reflections = null;
        }
      }
      // A room cube already supplies diffuse irradiance; avoid adding it twice.
      rig.probe.intensity = lighting.current.environment ? 0 : 1;
      lighting.current.status = lighting.current.environment ? "room-reflections" : "room-light";
      state.captured = true;
    }
    rig.group.userData.status = lighting.current.status;
  }, -2);
  return <primitive object={rig.group} />;
}
